import Decimal from 'decimal.js-light';
import { formatUnits, parseUnits } from 'viem';

import type { TokenInputValues } from '@/components/TransactionModule/store';

import type { TokenMetadata } from '@/utils/0x.types';

import { tokenFormatter } from '../../../lib/formatting';
import { isTokenAddressNativeAsset } from '../../../utils/multichain';
import { normalizeTokenAddress } from '../../../utils/normalizeAddress';
import { isNullAmount } from '../../../utils/numbers';
import { truncateDecimals } from '../../../utils/string';
import { BalanceFraction } from '../SellTokenQuickActions';

/**
 * Gets the token input values for the given decimal value, number of decimals and USD value.
 * @param decimalValue - The decimal value.
 * @param numOfDecimals - The number of decimals.
 * @param usdValue - The USD value.
 * @returns The token input values.
 */
export const getTokenInputValues = (
  decimalValue: string,
  numOfDecimals: number,
  usdValue: number,
): TokenInputValues => ({
  decimal: truncateDecimals(decimalValue, numOfDecimals),
  usd: usdValue,
  formatted: isNullAmount(parseFloat(decimalValue))
    ? ''
    : tokenFormatter.format(decimalValue, { mode: 'write' }),
});

/**
 * Converts a decimal amount to a token amount with the given number of decimals.
 * @param decimalAmount - The decimal amount to convert.
 * @param numOfDecimals - The number of decimals to use.
 * @returns The token amount.
 */
export const getTokenAmount = (
  decimalAmount: string,
  numOfDecimals: number | undefined,
): string | undefined =>
  typeof numOfDecimals === 'number' &&
  numOfDecimals >= 0 &&
  !Number.isNaN(parseFloat(decimalAmount)) &&
  !Number.isNaN(Number(decimalAmount))
    ? parseUnits(truncateDecimals(decimalAmount, numOfDecimals), numOfDecimals).toString()
    : undefined;

/** Chain-aware token address equality, treating each chain's native sentinel as equal. */
const isSameTokenAddress = (
  a: string | undefined,
  b: string | undefined,
  chainId: number | undefined,
): boolean => {
  if (!a || !b || chainId == null) return false;
  if (isTokenAddressNativeAsset(a, chainId) && isTokenAddressNativeAsset(b, chainId)) return true;
  return normalizeTokenAddress(a, chainId) === normalizeTokenAddress(b, chainId);
};

/**
 * Whether a resolved quote/price still matches the current selection (amount AND token pair).
 *
 * The price-success handler is re-created when sellAmount/sellToken/buyToken change, but the
 * query args are debounced — so the effect can re-fire with an in-flight quote for the previous
 * amount or token pair before the new quote resolves. Computing price impact (and buy formatting)
 * from such a quote pairs a stale buyAmount/decimals with the new selection, yielding a wildly
 * wrong impact and a flashed false warning. Callers should drop a quote this rejects.
 */
export const isQuoteForCurrentSelection = (
  quote: { sellAmount: string; sellToken: string; buyToken: string },
  current: {
    sellAmount: string | undefined;
    sellTokenAddress: string | undefined;
    buyTokenAddress: string | undefined;
    chainId: number | undefined;
  },
): boolean =>
  quote.sellAmount === current.sellAmount &&
  isSameTokenAddress(quote.sellToken, current.sellTokenAddress, current.chainId) &&
  isSameTokenAddress(quote.buyToken, current.buyTokenAddress, current.chainId);

/**
 * Determines if swap tokens have Fee-on-Transfer.
 */
export const hasFeeOnTransfer = (tokenMetadata: TokenMetadata | undefined): boolean => {
  if (!tokenMetadata) return false;

  return !!Number(tokenMetadata.buyToken.buyTaxBps) || !!Number(tokenMetadata.sellToken.sellTaxBps);
};

interface FeeOnTransferValue {
  feeOnTransferValue: number;
  feeOnTransferToken: string;
}

interface FeeOnTransferPair {
  buyTokenFee: FeeOnTransferValue | undefined;
  sellTokenFee: FeeOnTransferValue | undefined;
}

/**
 * Gets the fee on transfer token symbol and value.
 * @param currentPrice - The current price.
 * @param buyTokenSymbol - The buy token symbol.
 * @param sellTokenSymbol - The sell token symbol.
 * @returns The token symbol for which the fee applies and the fee value in bps
 */
export const getFeeOnTransferFromPair = ({
  tokenMetadata,
  buyTokenSymbol,
  sellTokenSymbol,
}: {
  tokenMetadata: TokenMetadata | undefined;
  buyTokenSymbol: string;
  sellTokenSymbol: string;
}): FeeOnTransferPair => {
  const feeValues: FeeOnTransferPair = { sellTokenFee: undefined, buyTokenFee: undefined };
  if (!hasFeeOnTransfer(tokenMetadata)) {
    return feeValues;
  }

  if (tokenMetadata) {
    const buyTaxBps = Number(tokenMetadata.buyToken.buyTaxBps);
    const sellTaxBps = Number(tokenMetadata.sellToken.sellTaxBps);
    if (buyTaxBps) {
      feeValues.buyTokenFee = { feeOnTransferValue: buyTaxBps, feeOnTransferToken: buyTokenSymbol };
    }
    if (sellTaxBps) {
      feeValues.sellTokenFee = {
        feeOnTransferValue: sellTaxBps,
        feeOnTransferToken: sellTokenSymbol,
      };
    }
  }

  return feeValues;
};

/**
 * Helper function to calculate input values based on the raw token amount,
 * token decimals, USD price, and fraction of the balance to use.
 */
export function calculateInput(
  rawAmount: bigint,
  decimals: number,
  usdPrice: number,
  fraction: BalanceFraction,
): TokenInputValues {
  const formattedFull = formatUnits(rawAmount, decimals);
  if (fraction === BalanceFraction.FULL) {
    return {
      usd: new Decimal(`0${formattedFull}`).mul(usdPrice).toNumber(),
      decimal: formattedFull,
      formatted: formattedFull,
    };
  }
  const halfDecimal = new Decimal(formattedFull).div(2).toString();
  return {
    usd: new Decimal(`0${halfDecimal}`).mul(usdPrice).toNumber(),
    decimal: halfDecimal,
    formatted: halfDecimal,
  };
}
